// BuildingGeometryKey_Pure.ts — Buildings Set2 §7 / F4: the FLOOR geometry-sharing key + the floor-level material-conflict diagnostics. PURE (no mobx-graphlink / three / DOM), so the Node vitest lane can audit the key's collision behaviour directly (F4's §10 audit item; the key used to live in the accessor module, which imports mobx-graphlink so nothing in it was testable).
import type {
	BuildingFloorPlan, ResolvedRoomMaterials,
} from "../../Server/DB/mapObjects/Building/BuildingGeometry.js";

/** A cheap deterministic string hash for the §6.4 geometry-sharing key (moved out of the accessor so the key is reproducible from the pure layer). */
export function HashString(s: string): string {
	let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
	for (let i = 0; i < s.length; i++) {
		const ch = s.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	// Return a fixed-width hex so the key is collision-safe-enough and stable.
	const a = (h1 >>> 0).toString(16).padStart(8, "0");
	const b = (h2 >>> 0).toString(16).padStart(8, "0");
	return a + b;
}

/** Set2 §7: the FLOOR's geometry-sharing key — a hash of everything that affects the floor's *shape* (the merged wall segments, the floor's openings, the room finish rings, the storey wall height and slab thickness, the per-room flags) but NOT the Z offset, so the identical floors of a 100-storey tower share one set of BufferGeometries. F4's audit of what participates is documented in the plan doc's §10 F4 note, and pinned by `BuildingWalls_F4.test.ts`. */
export function ComputeFloorGeometryKey(floor: BuildingFloorPlan): string {
	const parts: string[] = [];
	const walls = floor.walls;
	if (walls != null) {
		for (const s of walls.segments) {
			parts.push(`|s:${s.start[0].toFixed(3)},${s.start[1].toFixed(3)}->${s.end[0].toFixed(3)},${s.end[1].toFixed(3)}:${s.wallThickness.toFixed(3)}:${s.claimantRoomIDs.length}`);
		}
		// The finish rings participate because the slab/ceiling/finish geometry is built from them.
		for (const p of walls.innerFacePerRoom) {
			for (const q of p.ring) parts.push(`|f${p.roomObjID}:${q[0].toFixed(3)},${q[1].toFixed(3)}`);
		}
	}
	for (const group of floor.openingsBySegment) {
		for (const op of group.openings) {
			parts.push(`|o:${group.segmentIndex}:${op.kind}:${op.alongCenter.toFixed(3)}:${op.width.toFixed(3)}:${op.height.toFixed(3)}:${op.sillHeight.toFixed(3)}`);
			// P4 model params (swing, openAngle, pane divisions) all affect the model mesh, so they
			// MUST be part of the geometry key — two floors with identical walls but different door
			// swings share no geometry.
			const win = op.model.window, door = op.model.door;
			parts.push(`|m${op.kind}:dx:${win.paneDivisionsX}:dy:${win.paneDivisionsY}:sw:${door.swing}:oa:${door.openAngle.toFixed(2)}`);
		}
	}
	for (const room of floor.rooms) {
		parts.push(`|r${room.roomObjID}:w:${room.wallThickness.toFixed(3)}:ft:${room.floorThickness.toFixed(3)}:ct:${room.ceilingZ == null ? "-" : room.ceilingZ.toFixed(3)}`);
		parts.push(`|rf:${room.hasFloor ? 1 : 0}|rc:${room.hasCeiling ? 1 : 0}|ra:${room.hasFloorAbove ? 1 : 0}`);
	}
	parts.push(`|h:${(floor.wallTopZ - floor.floorTopZ).toFixed(3)}`);
	return HashString(parts.join(""));
}

/** The building-surface material slots the WALL layer renders with, in the order the conflict message lists them (the slots a shared/interior wall's claimants can disagree on while still producing identical geometry). */
export const WALL_MATERIAL_SLOTS: ReadonlyArray<keyof ResolvedRoomMaterials> = [
	"wallExterior", "wallInterior", "wallTop", "wallReveal",
];

/** Set2 §11 / F4: report the wall materials a floor's rooms DISAGREE on. Under the floor-level wall model a shared wall has TWO claimant rooms, so `wallExterior`/`wallInterior` are no longer unambiguously per-room — but v1 (F3) resolves a floor's material array from its FIRST room (`floor.rooms[0]`, in `Building3D.tsx`), so a multi-room floor whose rooms disagree renders EVERY wall in the first room's material, silently. This is the same failure class as the thickness conflict (§0 decision 4), so it gets the same treatment: keep the geometry, tell the author. Returns one message per disagreeing slot (not per room pair), so a 6-room floor with one odd room produces a short list instead of 15 lines. Pure + DB-free so the Node lane can drive it. */
export function DetectFloorMaterialConflicts_Pure(rooms: Array<{roomObjID: string, materials: ResolvedRoomMaterials}>): string[] {
	if (rooms.length < 2) return [];
	const out: string[] = [];
	for (const slot of WALL_MATERIAL_SLOTS) {
		const byRecipe = new Map<string, string[]>();
		for (const r of rooms) {
			const recipe = (r.materials as unknown as Record<string, string>)[slot as string] ?? "";
			const list = byRecipe.get(recipe) ?? [];
			list.push(r.roomObjID);
			byRecipe.set(recipe, list);
		}
		if (byRecipe.size < 2) continue;
		// Name every distinct recipe with the rooms that want it, so the author can see WHICH room
		// to change without hunting through the tree.
		const groups = [...byRecipe.entries()]
			.map(([recipe, ids])=>`"${recipe || "(none)"}" (rooms ${ids.join(", ")})`)
			.join(" vs ");
		out.push(`Wall material conflict on "${slot}" between this floor's rooms: ${groups}. v1 renders a floor's walls with its FIRST room's materials, so the other choice(s) are ignored (per-wall material overrides are deferred — see the plan's §11).`);
	}
	return out;
}
